/**
 * Contract aggregate. Immutable once signed.
 *
 * Why this is a separate aggregate from Loan:
 *   - The loan's terms can be re-amortised, written off, or modified by a
 *     dispute resolution. The contract — the legal document the customer
 *     signed — must NEVER change. Re-amortisation produces a NEW contract
 *     that references the previous one, and the old one is kept verbatim.
 *
 * The snapshot fields (loanTermsSnapshot, lockPolicySnapshot, ...) are
 * frozen JSON copies of the policy/terms/schedule at signature time. They
 * are what the contract PDF was rendered from; they are what we replay
 * during disputes. Never read them from "live" data.
 *
 * `jsonHash` is the SHA-256 of the canonical JSON snapshot, signed with the
 * contract-signing key from @dfp/crypto. Tampering with any snapshot field
 * breaks the hash and the contract is detected as invalid.
 *
 * Immutability is enforced by the construction API:
 *   - signContract(draft): Contract        ← only way to build one
 *   - Contract has no mutation functions
 *   - Repository attempting to UPDATE a contract row throws (P1.A.2)
 */
import { z } from "zod";
import { languageTag } from "../country/country.js";
import { gpsPointSchema } from "../geo.js";
const snapshotSchema = z.record(z.string(), z.unknown());
export const contractSchema = z.object({
    id: z.string().uuid(),
    countryId: z.string().uuid(),
    loanId: z.string().uuid(),
    templateId: z.string().min(1),
    version: z.number().int().positive(),
    language: z.string().min(2),
    signedAt: z.coerce.date(),
    signedByCustomer: z.string().min(1),
    signedByDealer: z.string().min(1),
    loanTermsSnapshot: snapshotSchema,
    deviceTermsSnapshot: snapshotSchema,
    lockPolicySnapshot: snapshotSchema,
    paymentScheduleSnapshot: snapshotSchema,
    pdfRef: z.string().min(1),
    jsonHash: z.string().regex(/^[0-9a-f]{64}$/i, "jsonHash must be 64-char hex SHA-256"),
    dealerGpsAtSignature: gpsPointSchema,
    dealerDeviceFingerprint: z.string().min(1),
});
/**
 * Construct a Contract from validated raw data. This is the *repository
 * load* path. The *signing* path lives in apps/api and uses @dfp/crypto to
 * compute `jsonHash` and sign it; the repository persists the result and
 * subsequent reads come back through `makeContract`.
 */
export function makeContract(raw) {
    const parsed = contractSchema.parse(raw);
    return {
        id: parsed.id,
        countryId: parsed.countryId,
        loanId: parsed.loanId,
        templateId: parsed.templateId,
        version: parsed.version,
        language: languageTag(parsed.language),
        signedAt: parsed.signedAt,
        signedByCustomer: parsed.signedByCustomer,
        signedByDealer: parsed.signedByDealer,
        loanTermsSnapshot: parsed.loanTermsSnapshot,
        deviceTermsSnapshot: parsed.deviceTermsSnapshot,
        lockPolicySnapshot: parsed.lockPolicySnapshot,
        paymentScheduleSnapshot: parsed.paymentScheduleSnapshot,
        pdfRef: parsed.pdfRef,
        jsonHash: parsed.jsonHash,
        dealerGpsAtSignature: parsed.dealerGpsAtSignature,
        dealerDeviceFingerprint: parsed.dealerDeviceFingerprint,
    };
}
/**
 * Canonical JSON used as input to the contract-hash computation. Keys are
 * sorted alphabetically and dates are ISO-8601; consumers who compute or
 * verify the hash MUST use this function so the bytes are identical
 * regardless of platform.
 */
export function canonicalContractJson(draft) {
    const canonical = {
        countryId: draft.countryId,
        dealerDeviceFingerprint: draft.dealerDeviceFingerprint,
        dealerGpsAtSignature: {
            accuracyMetres: draft.dealerGpsAtSignature.accuracyMetres,
            lat: draft.dealerGpsAtSignature.lat,
            lng: draft.dealerGpsAtSignature.lng,
        },
        deviceTermsSnapshot: sortKeys(draft.deviceTermsSnapshot),
        language: draft.language,
        loanId: draft.loanId,
        loanTermsSnapshot: sortKeys(draft.loanTermsSnapshot),
        lockPolicySnapshot: sortKeys(draft.lockPolicySnapshot),
        paymentScheduleSnapshot: sortKeys(draft.paymentScheduleSnapshot),
        pdfRef: draft.pdfRef,
        signedAt: draft.signedAt.toISOString(),
        signedByCustomer: draft.signedByCustomer,
        signedByDealer: draft.signedByDealer,
        templateId: draft.templateId,
        version: draft.version,
    };
    return JSON.stringify(canonical);
}
function sortKeys(value) {
    if (Array.isArray(value)) {
        return value.map(sortKeys);
    }
    if (value !== null && typeof value === "object") {
        const out = {};
        for (const key of Object.keys(value).sort()) {
            out[key] = sortKeys(value[key]);
        }
        return out;
    }
    return value;
}
//# sourceMappingURL=contract.js.map